import { useSuspenseInfiniteQuery } from '@tanstack/react-query'

import { getRequest } from 'src/lib/api-client'
import { queries } from 'src/lib/query-keys'

import type { EnterpriseMember } from '../schema/enterprise-schema'

export interface GetEnterpriseMembersParams {
  enterpriseId: number
  sortBy?: 'full_name' | 'role' | 'created_at'
  sortOrder?: 'asc' | 'desc'
  limit?: number
  offset?: number
  search?: string
}

export interface GetEnterpriseMembersResponse {
  memberships: EnterpriseMember[]
  totalCount: number
  limit: number
  offset: number
  sortBy: string
  sortOrder: string
}

const getEnterpriseMembers = async (params: GetEnterpriseMembersParams) => {
  const { enterpriseId, sortBy, sortOrder, limit, offset, search } = params

  const searchParams = new URLSearchParams()
  if (sortBy) searchParams.set('sort_by', sortBy)
  if (sortOrder) searchParams.set('sort_order', sortOrder)
  if (limit) searchParams.set('limit', limit.toString())
  if (offset) searchParams.set('offset', offset.toString())
  if (search) searchParams.set('search', search)

  const url = `/enterprise/${enterpriseId}/members?${searchParams.toString()}`

  const response = await getRequest<GetEnterpriseMembersResponse>(url)
  return response
}

export default function useGetEnterpriseMembers(params: GetEnterpriseMembersParams) {
  const pageSize = params.limit ?? 10

  return useSuspenseInfiniteQuery({
    ...queries.enterprise.membersSearch(
      params.enterpriseId,
      params.sortBy,
      params.sortOrder,
      params.limit,
      params.offset,
      params.search,
    ),
    queryFn: async ({ pageParam = 0 }) => {
      const response = await getEnterpriseMembers({ ...params, offset: pageParam })
      return {
        results: response || null,
      }
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      if (lastPage && lastPage.results?.memberships?.length === pageSize) {
        return pages.length * pageSize
      }
      return undefined
    },
    // select: (data) => ({
    //   pages: data.pages,
    //   pageParams: data.pageParams,
    // }),
    staleTime: 5 * 60 * 1000,
    retry: 3,
    retryDelay: 1000,
  })
}
